<script lang="ts">
  import DistributorName from "@shared/svelte/Components/ProductLines/DistributorName.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import CartWidget from "@shared/svelte/Components/ProductLines/ProductListCard/CartWidget.svelte";
  import SkuItemPrices from "@shared/svelte/Components/ProductLines/ProductListCard/SkuItemPrices.svelte";
  import ProductCardSalesDivisions from "@shared/svelte/Components/ProductLines/ProductCardSalesDivisions.svelte";
  import ProductCardVintages from "@shared/svelte/Components/ProductLines/ProductCardVintages.svelte";
  import { ProductLineListingStore } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductLineNavigationStore } from "@retailer/svelte/ProductDetails/stores/ProductLineNavigationStore";
  import { bottleTopsEnglish } from "@shared/filters/bottle_tops_english";
  import { productContainerDetailsShort } from "@shared/filters/product_container_details";
  import { showDealsToUser } from "@retailer/services/product_card_service";
  import { sizeAndUnitsDisplay } from "@shared/services/product";
  import { capitalize } from "lodash";
  import { goto } from "@roxi/routify";
  import {
    distributorIsSevenFiftyIntegrated,
    prepareSalesDivisions,
    showFuturePricing,
  } from "@retailer/services/product_card_service";
  import {
    ProductCardSkuItemEntity,
    ProductCardSalesDivisionsEntity,
    ProductCardSkuItemStockStatusEntity,
  } from "@shared/services/event_tracking/entities";
  import InventoryStatusStore from "@shared/svelte/Stores/InventoryStatusStore";

  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import type { DualProductDistributor } from "@shared/types/Distributor";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import { onDestroy } from "svelte";

  const { trackMrcEvents, trackClick } = EventTracking;

  export let productLine: ProductLine;
  export let product: Product;
  export let index: number;
  export let Cart: any;
  export let stateParams: any = {};
  export let productInventoryData: Product[] = [];

  let verifiedInStock: boolean = false;
  let priceScheduleAvailable: boolean = false;
  let futurePricesData: any = null;
  let inventoryStatusesFetched: boolean = false;
  let hasDeal: boolean = false;
  let productCardSkuItemStockStatusEntity: ProductCardSkuItemStockStatusEntity = null;

  const targetObjectType = "product_card_sku_item";

  $: mobileMode = $LayoutStore.isMobile;

  const productLineDualDistributors: DualProductDistributor[] =
    productLine.dual_product_distributors || [];

  // We only want to show the distributor name when we are NOT on the distributors PLP and when there is more than one distributor
  let showDistributorName: boolean =
    !(stateParams?.distributors || []).length && productLineDualDistributors.length > 1;
  let matchingDualDistributor: any = productLineDualDistributors.find(
    d => d.id == product.distributor_id,
  );

  const sevenFiftyIntegratedDistributor: boolean = distributorIsSevenFiftyIntegrated(
    matchingDualDistributor,
    productLine.distributor_info,
  );

  const unsubscribeInventoryStatus = InventoryStatusStore.subscribe(store => {
    inventoryStatusesFetched = Object.keys(store.inventoryStatuses || {}).length > 0;
    verifiedInStock =
      (store.inventoryStatuses[productLine.id] || []).find(
        inventoryStatus =>
          inventoryStatus.inventory_id.toString() === product.inventory_id.toString(),
      )?.verified_in_stock || false;
  });

  const unsubscribeListingStore = ProductLineListingStore.subscribe(n => {
    const discounts = n.discountData[productLine.id] || [];
    const hasDiscount = discounts.some(p => p?.inventory_id === product.inventory_id);
    hasDeal = showDealsToUser() && hasDiscount;
    futurePricesData = n.productLineFuturePrices[productLine.id]?.find(
      p => p?.inventory_id == product.inventory_id,
    );
    priceScheduleAvailable =
      !!futurePricesData && showFuturePricing(sevenFiftyIntegratedDistributor);
  });

  onDestroy(() => {
    unsubscribeInventoryStatus();
    unsubscribeListingStore();
  });

  const trackingParams: BaseEntityClass[] = [
    new ProductCardSkuItemEntity({
      position: index,
      product_id: product.id,
      distributor_id: product.distributor_id,
      inventory_id: product.inventory_id,
      deal_tag: hasDeal,
      // undefined values will remove from the entity, signifying the data was not available at all
      verified_in_stock: inventoryStatusesFetched ? verifiedInStock : undefined,
      price_schedule_available: priceScheduleAvailable ? priceScheduleAvailable : undefined,
    }),
  ];

  const salesDivisions = prepareSalesDivisions(
    productLine,
    matchingDualDistributor ? [matchingDualDistributor.id] : null,
  );

  const salesDivisionsEntity = ProductCardSalesDivisionsEntity.forSkuItem(salesDivisions);
  if (salesDivisionsEntity) {
    trackingParams.unshift(salesDivisionsEntity);
  }

  let productDetailPageHref = `/products/${productLine.id}`;

  $: {
    if (productLine.dual_product_distributors?.length && product.distributor_id) {
      productDetailPageHref = `/products/${productLine.id}?distributorId=${product.distributor_id}`;
    }
  }

  const onClickToPdp = trackingParams => {
    ProductLineNavigationStore.set({
      preSelectedContainerType: product.container_type,
      preSelectedVintage: product.vintage_year,
    });

    if (productCardSkuItemStockStatusEntity) {
      trackClick(targetObjectType, trackingParams.concat(productCardSkuItemStockStatusEntity));
    }

    $goto(productDetailPageHref);
  };

  let hoveringOnPdpLink: boolean = false;
  const enterSkuItem = () => {
    hoveringOnPdpLink = true;
  };

  const leaveSkuItem = () => {
    hoveringOnPdpLink = false;
  };

  const vintage =
    // @ts-ignore
    product.vintage_year && isNaN(product.vintage_year) ? null : parseInt(product.vintage_year);
</script>

<TrackingContext entities={trackingParams} let:trackingContext>
  <div class="fizz-stack-16" class:fizz-border-top={index > 0} />

  <div
    class="fizz-flex-vertical fizz-flow-16 fizz-stack-16"
    data-testid={`sku-item-${product.inventory_id}`}
    use:trackMrcEvents={[trackingContext.entities]}
  >
    <div class="fizz-space-between fizz-flex-row-responsive">
      <!-- container and sku info -->
      <!-- svelte-ignore a11y-click-events-have-key-events -->

      <div class="fizz-flex-row fizz-gap-32">
        <div class="fizz-flex-vertical">
          <a
            href={productDetailPageHref}
            class="fizz-flex-vertical fizz-underline-none fizz-text-inherit cursor-pointer"
            on:mouseover={enterSkuItem}
            on:focus={enterSkuItem}
            on:mouseleave={leaveSkuItem}
            on:click={() => {
              onClickToPdp(trackingContext.entities);
            }}
            data-testid={"sku-item-details"}
            style={mobileMode ? "" : "width: 105px;"}
          >
            <div class="fizz-flex-row-4">
              <span
                class="fizz-text-strong fizz-no-wrap"
                class:fizz-text-underline={hoveringOnPdpLink}
              >
                {sizeAndUnitsDisplay(product)}
                {#if product.container_type == "bottle" && product.material}
                  {capitalize(product.material)}
                {/if}
                {#if product.container_type == "bottle"}
                  {capitalize(bottleTopsEnglish(product.bottle_tops))}
                {/if}
                {capitalize(product.container_type)}
              </span>
              {#if vintage}
                <ProductCardVintages vintages={vintage} />
              {/if}
            </div>

            <p>{productContainerDetailsShort(product)}</p>
            {#if product.inventory.sku}
              <div class="fizz-text-small fizz-text-subdued" data-testid="sku-item-sku">
                SKU: {product.inventory.sku}
              </div>
            {/if}
          </a>
          <ProductCardSalesDivisions
            {productLine}
            {stateParams}
            skuItemSalesDivisions={salesDivisions}
          />
        </div>

        {#if showDistributorName && !mobileMode}
          <DistributorName distributor={matchingDualDistributor} />
        {/if}
      </div>

      <div class="fizz-flex-row-24">
        {#if !mobileMode}
          <CartWidget
            {product}
            {productLine}
            {Cart}
            {productInventoryData}
            on:openDrawer
            bind:trackingEntity={productCardSkuItemStockStatusEntity}
          />
        {/if}
      </div>
    </div>

    {#if mobileMode}
      {#if showDistributorName}
        <DistributorName distributor={matchingDualDistributor} />
      {/if}

      <!-- mobileMode displays prices above the cart widget -->
      <!-- desktop includes prices in the cart widget component so that they don't get misaligned by the pickup location length-->
      <div class="fizz-max-content">
        <SkuItemPrices {product} {productLine} />
      </div>

      <CartWidget
        {product}
        {productLine}
        {Cart}
        {productInventoryData}
        on:openDrawer
        bind:trackingEntity={productCardSkuItemStockStatusEntity}
      />
    {/if}
  </div>
</TrackingContext>
